import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { PlotlyComponent, PlotlyModule, PlotlyService } from 'angular-plotly.js';
import { PiechartPlotly } from './plots/piechart-plotly';
import { ChoroplethMapPlotly } from './plots/choropleth-map-plotly';
import { LineChartPlotly } from './plots/linechart-plotly';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { CustomDateRangeDialogComponent } from './date-picking/custom-date-range-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { delay, firstValueFrom, takeUntil } from 'rxjs';
import { InsightsService } from '@app/services/incidents/insights.service';
import { formatAsDateOnlyString, formatAsDateTimeStringIgnoringTheTime } from '@app/utils/datetime/datetime.utils';
import { InsightsPageStore } from './insights-page.store';
import { LINECHART_COLORS, PIECHART_COLORS } from './plots/plotly-colors';
import { MatTableModule } from '@angular/material/table';
import { PlotLegendComponent } from './plot-legend/plot-legend.component';
import * as Plotly from 'plotly.js-dist-min';
import { DateRangeValue } from './models/date-range.model';
import { PlotLegendData } from './models/plot-legend-data.model';
import { PlotLegendItemClicked } from './models/plot-legend-item-clicked.model';
import { CustomDateRangeData } from './models/custom-date-range-data.model';
import {
  ChoroplethMapData,
  ChoroplethMapGeoScopesByGeoLocationTabLabels,
} from './models/plots/choropleth-map-data.model';
import { ChoroplethMapInput } from './models/plots/choropleth-map-input.model';
import { LineChartTraceData } from './models/plots/linechart-trace-data.model';
import { LineChartTraceInput } from './models/plots/linechart-trace-input.model';
import { PiechartData } from './models/plots/piechart-data.model';
import { PieChartInput } from './models/plots/piechart-input.model';
import { PlotlyPlot } from './models/plots/plotly-plot.model';
import { MatMenuModule } from '@angular/material/menu';
import { AllDataComponent } from './all-data/all-data.component';
import { AllDataModel } from './models/all-data.model';
import { ChartAggregationType } from '@app/enums/chart-aggregation-type';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { PieChartPlotComponent } from './plots/piechart/piechart-plot.component';
import { DownloadAsPngData } from './models/download-as-png-data.model';
import { CountsByDataModel } from './models/counts-by-data.model';
import { InsightsChartSectionName, InsightsChartType } from './models/insights-chart.model';
import { SectionMenuComponent } from '@app/components/section-menu/section-menu.component';
import { Country, CountryDescriptor } from '@app/enums/country';
import { Continent, ContinentDescriptor } from '@app/enums/continent';
import { ImageDownloadService } from '@app/services/image-download/image-download.service';
import { DownloadAsCsvData } from './models/download-as-csv-data.model';

@Component({
  standalone: true,
  selector: 'app-insights-page',
  imports: [
    CommonModule,
    PlotlyModule,
    SectionMenuComponent,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
    MatSelectModule,
    MatTableModule,
    PlotLegendComponent,
    MatMenuModule,
    MatTabsModule,
    PieChartPlotComponent,
  ],
  providers: [
    InsightsPageStore, // Inject here to start a Scoped instance
  ],
  templateUrl: './insights-page.component.html',
  styleUrl: './insights-page.component.scss',
})
export class InsightsPageComponent implements OnInit {
  // Plotly related
  @ViewChild('incidentCount', { static: false }) incidentCountsLinechartElement?: PlotlyComponent;
  protected incidentCountsLinechart?: PlotlyPlot<LineChartTraceData>;
  protected incidentCountsLinechartData: PlotLegendData[] = [];

  protected incidentsByMotivationPieChart?: PlotlyPlot<PiechartData>;
  protected incidentsByMotivationPieChartData: PlotLegendData[] = [];

  // Geo-Locations
  @ViewChild('geoLocationsPlotlyElement', { static: false }) geoLocationsMapElement?: PlotlyComponent;
  protected get geoLocationsMapTabLabels(): string[] {
    return Object.keys(ChoroplethMapGeoScopesByGeoLocationTabLabels);
  }
  protected geoLocationsMap?: PlotlyPlot<ChoroplethMapData>;
  protected geoLocationsCountriesPieChart?: PlotlyPlot<PiechartData>;
  protected geoLocationsCountriesPieChartData: PlotLegendData[] = [];
  protected geoLocationsContinentsPieChart?: PlotlyPlot<PiechartData>;
  protected geoLocationsContinentsPieChartData: PlotLegendData[] = [];

  protected incidentsByThreatActorPieChart?: PlotlyPlot<PiechartData>;
  protected incidentsByThreatActorPieChartData: PlotLegendData[] = [];

  // Affected Entity Pie Charts
  protected incidentsByBusinessSectorPieChart?: PlotlyPlot<PiechartData>;
  protected incidentsByBusinessSectorPieChartData: PlotLegendData[] = [];

  protected incidentsByEntitySizePieChart?: PlotlyPlot<PiechartData>;
  protected incidentsByEntitySizePieChartData: PlotLegendData[] = [];

  // RIMS
  protected rimsTechnicalInitialAccessPieChart?: PlotlyPlot<PiechartData>;
  protected rimsTechnicalInitialAccessPieChartData: PlotLegendData[] = [];

  protected rimsPostExploitationPieChart?: PlotlyPlot<PiechartData>;
  protected rimsPostExploitationPieChartData: PlotLegendData[] = [];

  protected rimsTechnicalImpactPieChart?: PlotlyPlot<PiechartData>;
  protected rimsTechnicalImpactPieChartData: PlotLegendData[] = [];

  protected rimsNonTechnicalImpactPieChart?: PlotlyPlot<PiechartData>;
  protected rimsNonTechnicalImpactPieChartData: PlotLegendData[] = [];

  // Period selection
  protected datePeriod: FormGroup;

  protected dateRangeOptions: { value: DateRangeValue; label: string }[] = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: '3months', label: '3 Months' },
    { value: 'year', label: 'Year' },
    { value: 'all', label: 'All' },
    { value: 'custom', label: 'Custom Period' },
  ];

  protected selectedRangeValue: DateRangeValue = 'year'; // Setting the form values to the default - year. The display value is updated in the ngOnInit when calling onRangeSelectAsync();
  protected selectedPeriodDisplayValue = '';

  // Geo-location map zooming variables
  private readonly defaultZoomLevel = 1;
  private readonly zoomStep = 0.2;
  private zoomLevel = this.defaultZoomLevel;

  // In order to be able to use the enum in the HTML
  protected readonly InsightsChartType = InsightsChartType;

  constructor(
    protected readonly insightsPageStore: InsightsPageStore,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly insightsService: InsightsService,
    private readonly imageDownloadService: ImageDownloadService,
    private plotlyService: PlotlyService,
  ) {
    this.datePeriod = this.formBuilder.group({
      startEndDate: this.formBuilder.group({
        dateStart: [null],
        dateEnd: [null],
      }),
    });

    // Handle display value whenever new range is picked
    this.datePeriod
      .get('startEndDate')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.updatePeriodDisplayValue(value.dateStart, value.dateEnd);
      });

    // Relayout the incident counts line chart when the aggregation type changes.
    // This is needed because in some cases we hide the legend near the chart, so the chart needs to be refreshed to take the whole space.
    toObservable(this.insightsPageStore.chartAggregationType)
      .pipe(delay(50), takeUntilDestroyed())
      .subscribe(() => {
        const plotEl = this.incidentCountsLinechartElement?.plotEl.nativeElement;
        if (plotEl) {
          Plotly.relayout(plotEl, { autosize: true });
        }
      });
  }

  public async ngOnInit(): Promise<void> {
    // Default period is 1 year
    await this.onRangeSelectAsync('year');
  }

  // Getters

  protected get showIncidentCountsLineChartLegend(): boolean {
    // We want to hide the incident counts line chart when the aggregation type is year because the yealy data doesn't have previous periods. So, no need to have a legend.
    return this.insightsPageStore.chartAggregationType() !== ChartAggregationType.Year;
  }

  // Handlers

  protected incidentsCountLegendRowClicked(itemClicked: PlotLegendItemClicked, plotlyComponent: PlotlyComponent): void {
    // Don't allow the main period to be disabled. Only comparison/previous periods can be excluded.
    if (itemClicked.index === 0) {
      return;
    }

    // If the period was disabled we want to make it enabled and rerender the plot with that period
    if (itemClicked.row.disabled) {
      itemClicked.row.disabled = false;
      Plotly.restyle(plotlyComponent.plotEl.nativeElement, { visible: true }, [itemClicked.index]);
    }
    // If the period was selected/enabled we want to disable it and update the plot
    else {
      itemClicked.row.disabled = true;
      Plotly.restyle(plotlyComponent.plotEl.nativeElement, { visible: false }, [itemClicked.index]);
    }
  }

  protected async downloadAsPngClickedAsync(input: DownloadAsPngData): Promise<void> {
    const sectionName = InsightsChartSectionName[input.chartType];
    await this.imageDownloadService.downloadSectionAsPng(input.captureElement, sectionName);
  }

  // This handler is used when the image download service can't do the job as some complex structures may not be fully rendered and the screenshot may end up different.
  // It uses the PlotLy built in image download functionality so only the plot will be captured. Not the content around it.
  protected async downloadAsPngNativePlotlyClickedAsync(
    chartType: InsightsChartType,
    component: PlotlyComponent,
  ): Promise<void> {
    const plotly = await this.plotlyService.getPlotly();
    const plotElement = component.plotEl.nativeElement;

    plotly.downloadImage(plotElement, {
      format: 'png',
      filename: InsightsChartSectionName[chartType],
    });
  }

  protected async allDataClickedAsync(data: AllDataModel): Promise<void> {
    const dialogRef = this.dialog.open<AllDataComponent, AllDataModel, AllDataModel>(AllDataComponent, {
      width: '90vw',
      maxWidth: '1000px',
      disableClose: true,
      data,
    });

    dialogRef.componentInstance.downloadCsv
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe((csvData) => this.downloadAsCsvClickedAsync(csvData));
  }

  protected async downloadAsCsvClickedAsync(input: DownloadAsCsvData): Promise<void> {
    const escapeCsvValue = (value: string | number | null | undefined): string => {
      const stringValue = value == null ? '' : String(value);
      if (
        stringValue.includes(';') ||
        stringValue.includes('"') ||
        stringValue.includes('\n') ||
        stringValue.includes('\r')
      ) {
        // Wrap the entire value in double quotes to signal to CSV parsers that the content should be treated as a single field.
        // Also escaping any existing double quotes inside the string
        return `"${stringValue.replaceAll('"', '""')}"`;
      }

      return stringValue;
    };

    const includePercentage = input.chartType !== InsightsChartType.incidentCounts;

    const csvHeader = [input.labelColumnName, 'Count'];
    if (includePercentage) {
      // Only pie charts have percentages. For the incident counts keep the label and count only.
      csvHeader.push('Percentage');
    }

    const csvRows = input.data.map((row) => {
      const columns = [escapeCsvValue(row.label), escapeCsvValue(row.count)];
      if (includePercentage) {
        columns.push(escapeCsvValue(row.percentage == null ? '' : row.percentage.toFixed(2)));
      }

      return columns;
    });

    const csvContent = [csvHeader, ...csvRows].map((columns) => columns.join(';')).join('\n');
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const blobUrl = URL.createObjectURL(csvBlob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = `${InsightsChartSectionName[input.chartType]}.csv`;
    anchor.click();

    URL.revokeObjectURL(blobUrl);
  }

  // Date period picking

  protected async onRangeSelectAsync(rangeValue: DateRangeValue): Promise<void> {
    const today = new Date();
    let startDate: Date | null = null;
    let endDate: Date = new Date(today);

    switch (rangeValue) {
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        break;
      case 'month':
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        break;
      case '3months':
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'year':
        startDate = new Date(today);
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      case 'all':
        // when all is selected, we don't send a start date. The backend then sends back the
        // earliest incident's start date fetched from the db. It's used as a start date for the generated period.
        break;
      case 'custom': {
        const result = await this.openCustomDateDialogAsync();

        if (result) {
          startDate = result.startDate;
          endDate = result.endDate;
        } else {
          // Dialog was cancelled, don't update data
          return;
        }
        break;
      }
      default:
        return;
    }

    this.datePeriod.get('startEndDate')?.patchValue({
      dateStart: startDate,
      dateEnd: endDate,
    });

    // Directly update the data when a new period is chosen
    await this.loadDataAsync();
  }

  protected onContinentSelectedTabChange(event: MatTabChangeEvent): void {
    const continent = event.tab.textLabel as keyof typeof ChoroplethMapGeoScopesByGeoLocationTabLabels;
    this.loadGeoLocationsChoroplethMap(continent);

    // Reset the zoom value for the map
    this.zoomLevel = this.defaultZoomLevel;
  }

  protected onGeoLocationsMapZoomInClicked(): void {
    this.zoomLevel += this.zoomStep;
    this.relayoutGeoLocationsMap();
  }

  protected onGeoLocationsMapZoomOutClicked(): void {
    this.zoomLevel = Math.max(this.defaultZoomLevel, this.zoomLevel - this.zoomStep);
    this.relayoutGeoLocationsMap();
  }

  protected onGeoLocationsMapZoomResetClicked(): void {
    this.zoomLevel = this.defaultZoomLevel;
    this.relayoutGeoLocationsMap(true);
  }

  private relayoutGeoLocationsMap(resetPosition = false): void {
    const plotEl = this.geoLocationsMapElement?.plotEl.nativeElement;
    if (!plotEl) {
      return;
    }

    const update: Record<string, unknown> = {
      'geo.projection.scale': this.zoomLevel,
    };

    if (resetPosition) {
      // Reset pan/drag back to Plotly default center for the current scope
      update['geo.center'] = null;
      update['geo.projection.rotation'] = null;
    }

    Plotly.relayout(plotEl, update as Partial<Plotly.Layout>);
  }

  private async openCustomDateDialogAsync(): Promise<CustomDateRangeData | null> {
    const currentDates = this.datePeriod.get('startEndDate')?.value;

    const dialogRef = this.dialog.open<CustomDateRangeDialogComponent, CustomDateRangeData, CustomDateRangeData | null>(
      CustomDateRangeDialogComponent,
      {
        disableClose: true,
        data: {
          startDate: currentDates?.dateStart || null,
          endDate: currentDates?.dateEnd || null,
        },
      },
    );

    return (await firstValueFrom(dialogRef.afterClosed())) ?? null;
  }

  private updatePeriodDisplayValue(startDate: Date | null, endDate: Date | null): void {
    if (!startDate && endDate) {
      // No start date is specified. Fetching a start date from the backend response object.
      const earliestStartDate: string | null = this.insightsPageStore.earliestIncidentStartDate();
      if (earliestStartDate) {
        this.selectedPeriodDisplayValue = `${formatAsDateOnlyString(earliestStartDate)} - ${formatAsDateOnlyString(endDate)}`;
        return;
      }
    }

    if (startDate && endDate) {
      this.selectedPeriodDisplayValue = `${formatAsDateOnlyString(startDate)} - ${formatAsDateOnlyString(endDate)}`;
      return;
    }

    this.selectedPeriodDisplayValue = '';
  }

  // Data loading

  private async loadDataAsync(): Promise<void> {
    const startEndDate = this.datePeriod.get('startEndDate')?.value;
    const requestFilters = {
      startDate: startEndDate?.dateStart
        ? formatAsDateTimeStringIgnoringTheTime(startEndDate?.dateStart as Date)
        : undefined,
      endDate: formatAsDateTimeStringIgnoringTheTime(startEndDate?.dateEnd as Date),
    };

    const responseInsights = await firstValueFrom(this.insightsService.getInsights(requestFilters));
    this.insightsPageStore.setData(responseInsights);

    // Load the charts
    this.loadIncidentCountsLineChart();
    this.loadIncidentByMotivationPieChart();
    this.loadGeoLocationsChoroplethMap('Global');
    this.loadGeoLocationsCountriesPieChart();
    this.loadGeoLocationsContinentsPieChart();
    this.loadIncidentByThreatActorPieChart();
    this.loadIncidentsByAffectedEntityPieCharts();
    this.loadRimsStepsByPhasePieCharts();
  }

  // Plotly creation
  // TODO: Consider abstracting this logic to separate classes responsible for each plot.

  private loadIncidentCountsLineChart(): void {
    const periods = this.insightsPageStore.incidentCountsForMultiplePeriods();
    const xLabels = this.insightsPageStore.incidentCountsAggregationLabels();

    if (!periods || !xLabels) {
      return;
    }

    // "trace" in plotly means one line plot. We can have multiple traces (plotted lines) in one line chart.
    // xLabels -> used for every trace and passed as it's x values. That way we plot each trace data on top of each other because they have the same x axis values.
    //            That's why we pass them directly at the LineChartPlotly.create(), as they are the same for each trace.
    // xValuesPeriod -> the actual x values for a certain period. These values are shown when the user hover over a period point located at (X, Y)
    // yValues -> the incident counts for a period. The values are directly passed for each trace
    const lineChartTraces: LineChartTraceInput[] = periods.map((p, i) => ({
      yValues: p.counts,
      xValuesPeriod: p.aggregationValues.map((aggregationValue) => aggregationValue),
      color: LINECHART_COLORS[i],
      disabled: i === 0 ? false : true, // by default, enable/show only the first period
    }));

    // update the "legend" data:
    this.incidentCountsLinechartData = [
      ...lineChartTraces.map(
        (trace, index) =>
          ({
            label: `${formatAsDateOnlyString(periods[index].startDate)} - ${formatAsDateOnlyString(periods[index].endDate)}`,
            disabled: trace.disabled,
            color: LINECHART_COLORS[index],
          }) as PlotLegendData,
      ),
    ];

    this.incidentCountsLinechart = LineChartPlotly.create(
      lineChartTraces,
      xLabels,
      'Time',
      'Incidents',
      this.insightsPageStore.chartAggregationType()?.toString() ?? '',
      'Incidents',
    );
  }

  private loadIncidentByMotivationPieChart(): void {
    const incidents = this.insightsPageStore.incidentsByMotivationConsolidated();

    if (!incidents) {
      return;
    }

    // update the "legend" data:
    this.incidentsByMotivationPieChartData = incidents.labels.map(
      (label, index) =>
        ({
          label: label,
          count: incidents.counts[index],
          percentage: incidents.percentages[index],
          color: PIECHART_COLORS.green[index],
        }) as PlotLegendData,
    );

    const pieChartInput: PieChartInput = {
      labels: incidents.labels,
      values: incidents.counts,
      colors: PIECHART_COLORS.green,
    };

    this.incidentsByMotivationPieChart = PiechartPlotly.create(pieChartInput);
  }

  private loadGeoLocationsChoroplethMap(
    continentFilter: keyof typeof ChoroplethMapGeoScopesByGeoLocationTabLabels,
  ): void {
    const geoLocations = this.insightsPageStore.geoLocations();
    if (!geoLocations) {
      return;
    }

    const isGlobalContinentFilter = continentFilter == 'Global';

    const countriesCounts: number[] = [];
    const countriesGroupedKeys: Country[] = [];
    const countriesPercentages: number[] = [];
    for (const geoLocation of geoLocations.filter(
      (geoLocation) =>
        isGlobalContinentFilter ||
        geoLocation.continent ==
          (Object.keys(ContinentDescriptor) as (keyof typeof Continent)[]).find(
            (key) => ContinentDescriptor[key] == continentFilter,
          ),
    )) {
      countriesCounts.push(...geoLocation.countryCounts);
      countriesGroupedKeys.push(...geoLocation.countryGroupedKeys);
      countriesPercentages.push(
        ...(isGlobalContinentFilter
          ? geoLocation.countryPercentagesToGlobal
          : geoLocation.countryPercentagesToContinent),
      );
    }

    // Since a country can span over multiple continents we want to remove duplicates in Global mode
    if (isGlobalContinentFilter) {
      const countries: string[] = [];
      const indexesToRemove: number[] = [];
      for (let index = 0; index < countriesGroupedKeys.length; index++) {
        if (countries.includes(countriesGroupedKeys[index])) {
          indexesToRemove.push(index);
        } else {
          countries.push(countriesGroupedKeys[index]);
        }
      }

      for (const indexToRemove of indexesToRemove) {
        countriesCounts.splice(indexToRemove, 1);
        countriesGroupedKeys.splice(indexToRemove, 1);
        countriesPercentages.splice(indexToRemove, 1);
      }
    }

    const choroplethInput: ChoroplethMapInput = {
      countryCodes: countriesGroupedKeys,
      locationDisplayNames: countriesGroupedKeys.map((countryCode) => CountryDescriptor[countryCode]),
      locationValues: countriesCounts,
      continentScope: ChoroplethMapGeoScopesByGeoLocationTabLabels[continentFilter],
    };

    this.geoLocationsMap = ChoroplethMapPlotly.create(choroplethInput);
  }

  private loadGeoLocationsCountriesPieChart(): void {
    const geoLocationsCountriesConsolidated = this.insightsPageStore.geoLocationsCountriesConsolidated();

    if (!geoLocationsCountriesConsolidated) {
      return;
    }

    // update the "legend" data:
    this.geoLocationsCountriesPieChartData = geoLocationsCountriesConsolidated.labels.map(
      (label, index) =>
        ({
          label: label,
          count: geoLocationsCountriesConsolidated.counts[index],
          percentage: geoLocationsCountriesConsolidated.percentages[index],
          color: PIECHART_COLORS.blue[index],
        }) as PlotLegendData,
    );

    const pieChartInput: PieChartInput = {
      labels: geoLocationsCountriesConsolidated.labels,
      values: geoLocationsCountriesConsolidated.counts,
      colors: PIECHART_COLORS.blue,
    };

    this.geoLocationsCountriesPieChart = PiechartPlotly.create(pieChartInput);
  }

  private loadGeoLocationsContinentsPieChart(): void {
    const geoLocationsContinentsConsolidated = this.insightsPageStore.geoLocationsContinentsConsolidated();

    if (!geoLocationsContinentsConsolidated) {
      return;
    }

    // update the "legend" data:
    this.geoLocationsContinentsPieChartData = geoLocationsContinentsConsolidated.labels.map(
      (label, index) =>
        ({
          label: label,
          count: geoLocationsContinentsConsolidated.counts[index],
          percentage: geoLocationsContinentsConsolidated.percentages[index],
          color: PIECHART_COLORS.blue[index],
        }) as PlotLegendData,
    );

    const pieChartInput: PieChartInput = {
      labels: geoLocationsContinentsConsolidated.labels,
      values: geoLocationsContinentsConsolidated.counts,
      colors: PIECHART_COLORS.blue,
    };

    this.geoLocationsContinentsPieChart = PiechartPlotly.create(pieChartInput);
  }

  private loadIncidentByThreatActorPieChart(): void {
    const incidents = this.insightsPageStore.incidentsByThreatActorConsolidated();

    if (!incidents) {
      return;
    }

    // update the "legend" data:
    this.incidentsByThreatActorPieChartData = incidents.labels.map(
      (label, index) =>
        ({
          label: label,
          count: incidents.counts[index],
          percentage: incidents.percentages[index],
          color: PIECHART_COLORS.purple[index],
        }) as PlotLegendData,
    );

    const pieChartInput: PieChartInput = {
      labels: incidents.labels,
      values: incidents.counts,
      colors: PIECHART_COLORS.purple,
    };

    this.incidentsByThreatActorPieChart = PiechartPlotly.create(pieChartInput);
  }

  private loadIncidentsByAffectedEntityPieCharts(): void {
    const byBusinessSector = this.insightsPageStore.incidentsByBusinessSectorConsolidated();
    const byEntitySize = this.insightsPageStore.incidentsByEntitySize();

    if (!byEntitySize || !byBusinessSector) {
      return;
    }

    // update the "legend" data:
    this.incidentsByBusinessSectorPieChartData = byBusinessSector.labels.map(
      (label, index) =>
        ({
          label: label,
          count: byBusinessSector.counts[index],
          percentage: byBusinessSector.percentages[index],
          color: PIECHART_COLORS.green[index],
        }) as PlotLegendData,
    );

    this.incidentsByEntitySizePieChartData = byEntitySize.labels.map(
      (label, index) =>
        ({
          label: label,
          count: byEntitySize.counts[index],
          percentage: byEntitySize.percentages[index],
          color: PIECHART_COLORS.green[index],
        }) as PlotLegendData,
    );

    // create the plots
    const byEntityTypePieChartInput: PieChartInput = {
      labels: byBusinessSector.labels,
      values: byBusinessSector.counts,
      colors: PIECHART_COLORS.green,
    };

    const byEntitySizePieChartInput: PieChartInput = {
      labels: byEntitySize.labels,
      values: byEntitySize.counts,
      colors: PIECHART_COLORS.green,
    };

    this.incidentsByBusinessSectorPieChart = PiechartPlotly.create(byEntityTypePieChartInput);
    this.incidentsByEntitySizePieChart = PiechartPlotly.create(byEntitySizePieChartInput);
  }

  private loadRimsStepsByPhasePieCharts(): void {
    [this.rimsTechnicalInitialAccessPieChartData, this.rimsTechnicalInitialAccessPieChart] =
      loadRimsStepsByPhasePieChart(this.insightsPageStore.rimsStepsByPhases_TechnicalInitialAccess_Consolidated());

    [this.rimsPostExploitationPieChartData, this.rimsPostExploitationPieChart] = loadRimsStepsByPhasePieChart(
      this.insightsPageStore.rimsStepsByPhases_PostExploitation_Consolidated(),
    );

    [this.rimsTechnicalImpactPieChartData, this.rimsTechnicalImpactPieChart] = loadRimsStepsByPhasePieChart(
      this.insightsPageStore.rimsStepsByPhases_TechnicalImpact_Consolidated(),
    );

    [this.rimsNonTechnicalImpactPieChartData, this.rimsNonTechnicalImpactPieChart] = loadRimsStepsByPhasePieChart(
      this.insightsPageStore.rimsStepsByPhases_NonTechnicalImpact_Consolidated(),
    );

    function loadRimsStepsByPhasePieChart(
      consolidatedItems: CountsByDataModel | null,
    ): [PlotLegendData[], PlotlyPlot<PiechartData> | undefined] {
      if (!consolidatedItems) {
        return [[], undefined];
      }

      const piechartData: PlotLegendData[] = consolidatedItems.labels.map(
        (label, index) =>
          ({
            label: label,
            count: consolidatedItems.counts[index],
            percentage: consolidatedItems.percentages[index],
            color: PIECHART_COLORS.orange[index],
          }) as PlotLegendData,
      );

      const byEntityTypePieChartInput: PieChartInput = {
        labels: consolidatedItems.labels,
        values: consolidatedItems.counts,
        colors: PIECHART_COLORS.orange,
      };

      const plotlyObj = PiechartPlotly.create(byEntityTypePieChartInput);

      return [piechartData, plotlyObj];
    }
  }
}
